// mui
import { useTheme } from '@mui/material/styles';
import {
  Box,
  MenuItem,
  Rating,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
//
import { Iconify, Label } from '@/components/';
import { TableMoreMenu, TableRowLink } from '@/components/table';
import DialogSimple from '@/components/dialog/DialogSimple';
// utils + hooks
import { providersPath } from '../../routes/paths';
import useToggle from '@/hooks/useToggle';
import { use3DotMenu } from '@/hooks/use3DotMenu';
import { fHundredsPlus } from '@/utils/formatNumber';
// types
import { LabelColors } from '@/types';
import { User } from '@/features/users/shared/types';

type RowProps = {
  row: User;
  selected: boolean;
  onEditRow: VoidFunction;
  onDeleteRow: VoidFunction;
  //
  canDelete: boolean;
  canUpdate: boolean;
};

// ----------------------------------------------------------------------
export default function ProviderTableRow({
  row,
  onEditRow,
  onDeleteRow,
  canDelete,
  canUpdate,
}: RowProps) {
  const theme = useTheme();

  const {
    id,
    first_name,
    last_name,
    email,
    created_at,
    user_statuses,
    rating,
    jobs_completed,
    is_chat_user,
  } = row;

  // format Created At Date
  const createdAtFormatted = new Date(created_at).toLocaleDateString('en-gb', {
    weekday: 'long',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  // ===================

  const { openMenu, handleCloseMenu, handleOpenMenu } = use3DotMenu();

  // ========================
  //      DELETE PROVIDER
  // ========================
  const {
    toggle: isDeleteDialogOpen,
    onOpen: openDeleteDialog,
    onClose: closeDeleteDialog,
  } = useToggle();

  const handleDelete = () => {
    onDeleteRow();
    closeDeleteDialog();
  };

  return (
    <>
      <DialogSimple
        dialogTitle={`Delete provider: ${first_name} ${last_name}`}
        open={isDeleteDialogOpen}
        onClickButtonClose={closeDeleteDialog}
        onClickButtonLoading={handleDelete}
        loadingButton={true}
        disagreeButton={true}
        loadingButtonText="Delete"
        loadingButtonColor="error"
        disagreeButtonText="Cancel"
        disagreeButtonColor="inherit"
      >
        <Box sx={{ px: 3 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to delete this provider:{' '}
            <span style={{ fontWeight: 600 }}>
              {first_name} {last_name}
            </span>
            ?
          </Typography>
        </Box>
      </DialogSimple>

      <TableRow hover sx={{ verticalAlign: 'center' }}>
        <TableCell width={5} />

        <TableCell>
          <TableRowLink
            linkText={`${first_name} ${last_name}`}
            linkTo={providersPath('edit', {
              userId: String(id),
              tabSid: 'details',
            })}
            noWrap
          />
        </TableCell>

        <TableCell align="left">
          <TableRowLink linkText={email} linkTo={`mailto:${email}`} />
        </TableCell>

        <TableCell align="left">
          <Rating
            name="rating"
            defaultValue={rating}
            precision={0.5}
            readOnly
          />
        </TableCell>

        <TableCell align="left">{fHundredsPlus(jobs_completed)}</TableCell>

        <TableCell align="left">
          <Iconify
            icon={
              is_chat_user
                ? 'eva:checkmark-circle-2-fill'
                : 'eva:close-circle-outline'
            }
            color={is_chat_user ? 'green' : 'gray'}
          />
        </TableCell>

        <TableCell align="left">{createdAtFormatted}</TableCell>

        <TableCell align="left">
          <Label
            variant={theme.palette.mode === 'light' ? 'ghost' : 'filled'}
            color={
              user_statuses
                ? (user_statuses.style_class as LabelColors)
                : 'default'
            }
            sx={{ textTransform: 'capitalize' }}
          >
            {user_statuses ? user_statuses.value : 'N/A'}
          </Label>
        </TableCell>

        <TableCell align="right">
          {(canDelete || canUpdate) && (
            <TableMoreMenu
              open={openMenu}
              onOpen={handleOpenMenu}
              onClose={handleCloseMenu}
              actions={
                <>
                  {canUpdate && (
                    <MenuItem
                      onClick={() => {
                        onEditRow();
                        handleCloseMenu();
                      }}
                    >
                      <Iconify icon={'eva:edit-fill'} />
                      Edit
                    </MenuItem>
                  )}
                  {canDelete && (
                    <MenuItem
                      onClick={() => {
                        openDeleteDialog();
                        handleCloseMenu();
                      }}
                      sx={{ color: 'error.main' }}
                    >
                      <Iconify icon={'eva:trash-2-outline'} />
                      Delete
                    </MenuItem>
                  )}
                </>
              }
            />
          )}
        </TableCell>
      </TableRow>
    </>
  );
}
